import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Check } from 'lucide-react';
import { COMPANY, FORMSUBMIT_URL } from '../data/site';

const inputCls =
  'w-full bg-transparent border border-clay px-4 py-3.5 text-sm text-ink outline-none rounded-none placeholder:text-ink/35 focus:border-terra transition-colors duration-200';

const INFO = [
  { icon: MapPin, label: 'OFFICE ADDRESS', value: COMPANY.address, testid: 'contact-address' },
  { icon: Phone, label: 'PHONE', value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, testid: 'contact-phone' },
  { icon: Mail, label: 'EMAIL', value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: 'contact-email' },
  { icon: Clock, label: 'OFFICE TIMINGS', value: COMPANY.hours, testid: 'contact-hours' },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(FORMSUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ _subject: `Contact Form — ${form.subject || 'General Enquiry'}`, ...form }),
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <main data-testid="contact-page" className="pt-40 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">TALK TO US — HEAD OFFICE</p>
        <motion.h1
          initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink mb-16"
          data-testid="contact-headline"
        >
          Contact
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="border-t border-clay">
              {INFO.map((item) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5 }}
                  className="flex gap-5 py-6 border-b border-clay"
                  data-testid={item.testid}
                >
                  <span className="w-10 h-10 bg-terra/10 text-terra flex items-center justify-center shrink-0"><item.icon size={16} /></span>
                  <div>
                    <p className="text-[9px] tracking-[0.26em] text-ink/40 font-medium mb-1.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-sm text-ink font-medium leading-relaxed hover:text-terra transition-colors duration-200">{item.value}</a>
                    ) : (
                      <p className="text-sm text-ink font-medium leading-relaxed">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="mt-10 border border-clay bg-stone"
              data-testid="contact-map"
            >
              <iframe
                title="Prakruti Homes office — Androon Quilla, Raichur"
                src="https://www.google.com/maps?q=Androon%20Quilla%2C%20Raichur%2C%20Karnataka%20584101&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-72 grayscale-[35%] contrast-[1.05]"
                loading="lazy"
              />
              <p className="px-5 py-4 text-xs text-ink/50 flex items-center gap-2">
                <MapPin size={12} className="text-terra" /> 1st Floor, Kille Bruhanmatha Complex, Androon Quilla, Raichur
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-stone border border-clay p-8 lg:p-12"
            >
              {status === 'sent' ? (
                <div className="py-20 text-center" data-testid="contact-success">
                  <span className="w-14 h-14 bg-moss text-sand flex items-center justify-center mx-auto mb-6"><Check size={24} /></span>
                  <h3 className="font-heading font-bold text-2xl tracking-tight text-ink mb-3">Enquiry sent</h3>
                  <p className="text-sm text-ink/60 max-w-sm mx-auto">Thank you. Our team will reach out within one working day — or call us right now on {COMPANY.phone}.</p>
                </div>
              ) : (
                <>
                  <p className="text-[10px] tracking-[0.28em] text-terra font-medium mb-3">SEND ENQUIRY</p>
                  <h2 className="font-heading font-extrabold tracking-tighter text-3xl text-ink mb-8">We reply within one working day</h2>
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">NAME</label>
                        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">PHONE</label>
                        <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-input-phone" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">EMAIL</label>
                      <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">SUBJECT</label>
                      <input type="text" required placeholder="e.g. Plot availability at Prakruti Meadows" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-input-subject" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">MESSAGE</label>
                      <textarea rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" className={`${inputCls} resize-none`} />
                    </div>
                    {status === 'error' && (
                      <p className="text-xs text-terra" data-testid="contact-error">Something went wrong. Please email {COMPANY.email} directly.</p>
                    )}
                    <button type="submit" disabled={status === 'sending'} data-testid="contact-submit-btn"
                      className="group bg-moss text-sand text-[11px] font-medium tracking-[0.22em] px-10 py-4 flex items-center gap-2 hover:bg-terra transition-colors duration-300 disabled:opacity-60">
                      {status === 'sending' ? 'SENDING…' : 'SEND ENQUIRY'}
                      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
}
